import React, { useState, useEffect } from 'react'
import styled, { keyframes, css } from 'styled-components'
import StatsBar from './StatsBar';

const BannerContainer = styled.div`
  display: flex;
  height: auto;
  min-height: 100vh;
  overflow-x: hidden;
  
  @media (min-width: 768px) {
    height: 70vh;
  }
`

const Section = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 2rem 0.75rem;
  
  @media (min-width: 768px) {
    justify-content: center;
    padding: 2rem;
  }

  h2 {
    margin-top: 2rem;
    margin-bottom: 1.5rem;
    font-size: 1.3rem;
    padding: 0 1rem;
    text-align: center;
    
    @media (min-width: 768px) {
      margin-top: 5rem;
      margin-bottom: 2rem;
      font-size: 2rem;
      padding: 0;
    }
  }
`

const ToggleContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`

const SwitchContainer = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 30px;
  transform: rotate(90deg);
  
  @media (min-width: 768px) {
    width: 80px;
    height: 40px;
  }
`

const SwitchInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background: ${props => props.variant === 'blue' 
      ? 'linear-gradient(145deg, #0c2198 0%, #4254b5 100%)'
      : 'linear-gradient(145deg, #d71d20 0%, #ff4b4e 100%)'};
    border: 2px solid white;
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.2),
                0 0 10px ${props => props.variant === 'blue' 
                  ? 'rgba(12,33,152,0.3)'
                  : 'rgba(215,29,32,0.3)'};
  }
`

const ToggleTitle = styled.div`
    width: 80%;
    position: absolute;
    top: 8%;
    left: 10%;
    border-radius: 10px;
    // background:rgba(0,0,0,0.3);
    color: black;
    font-size: 2rem;
    font-weight: 300 !important;
    font-family: 'Courier New', Courier, monospace;
    text-align: center;
    margin-bottom: 2rem;
    z-index: 100;
    opacity: 0;
    visibility: hidden;
    animation: ${props => props.show ? fadeIn : 'none'} 3s ease-out forwards;
    animation-delay: 0.2s;

    ${props => props.show && `
      opacity: 1;
      visibility: visible;
    `}
`

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(145deg, #d1d1d1 0%, #f5f5f5 100%);
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  border-radius: 40px;
  border: 2px solid white;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.1),
              0 0 5px rgba(0,0,0,0.1);

  &:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    transform: scale(1.05);
  }
`

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.1);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
`;

const AnimatedToggleWrapper = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }

  animation: ${pulseAnimation} 2s infinite;
  transition: transform 0.3s ease;

  /* Variant-specific colors for the pulse effect */
  ${props => props.variant === 'red' && css`
    &:hover {
      box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.1);
    }
    animation: ${pulseAnimation} 2s infinite cubic-bezier(0.66, 0, 0, 1);
  `}

  ${props => props.variant === 'blue' && css`
    &:hover {
      box-shadow: 0 0 0 2px rgba(66, 84, 181, 0.1);
    }
    animation: ${pulseAnimation} 2.2s infinite cubic-bezier(0.66, 0, 0, 1);
  `}

  ${props => props.variant === 'green' && css`
    &:hover {
      box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.1);
    }
    animation: ${pulseAnimation} 2.4s infinite cubic-bezier(0.66, 0, 0, 1);
  `}
`;

const Toggle = ({ isChecked, onChange, variant }) => (
  <SwitchContainer>
    <SwitchInput 
      type="checkbox" 
      checked={isChecked}
      onChange={onChange}
      variant={variant}
    />
    <Slider checked={isChecked} variant={variant} />
  </SwitchContainer>
)

const typeWriter = keyframes`
  from {
    width: 0;
    border-right: 2px solid #00ff00;
  }
  to {
    width: 100%;
    border-right: 2px solid transparent;
  }
`

const fadeOut = keyframes`
  from { opacity: 1 }
  to { opacity: 0 }
`

const fadeIn = keyframes`
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`

const MatrixText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 1200px;
  text-align: center;
  animation: ${props => props.fadeOut ? fadeOut : 'none'} 1s forwards;
  padding: 0 20px;
  display: flex;
  justify-content: center;
`

const TypewriterText = styled.h1`
  position: relative;
  width: 100%;
  margin: 0 auto;
  letter-spacing: 0.1em;
  color: transparent;
  font-family: 'Courier New', Courier, monospace;
  font-size: 1.8rem;
  line-height: 1.4;
  padding: 0 1rem;
  
  @media (min-width: 768px) {
    font-size: 5rem;
    line-height: 1.5;
    padding: 0;
  }

  &::before {
    content: "${props => props.text}";
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    color: #00ff00;
    text-shadow: 0 0 3px #00ff00, 0 0 6px #00ff00;
    overflow: hidden;
    white-space: pre-wrap;
    word-wrap: break-word;
    animation: ${typeWriter} 3s steps(100, end) forwards;
  }
`

const ContentContainer = styled.div`
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  padding: 0 0.5rem;
  
  @media (min-width: 768px) {
    height: 100%;
    flex-direction: row;
    padding: 0;
  }
`

const listItemFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: center;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    max-width: none;
  }
`;

const ListItem = styled.li`
  margin-bottom: 1.5rem;
  padding: 1.25rem 1rem;
  width: calc(100% - 2rem);
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
  
  @media (min-width: 768px) {
    margin-bottom: 2rem;
    padding: 2rem;
    width: 100%;
    max-width: 600px;
  }

  opacity: 0;
  animation: ${listItemFadeIn} 0.5s ease-out forwards;
  animation-delay: ${props => props.delay}s;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
  
  h3 {
    margin-bottom: 0.75rem;
    color: #333;
    font-size: 1.1rem;
    font-weight: 600;
    padding: 0;
    
    @media (min-width: 768px) {
      font-size: 1.5rem;
      margin-bottom: 1rem;
    }
  }

  p {
    max-width: 100%;
    margin: 0 auto;
    color: #666;
    line-height: 1.5;
    font-size: 0.9rem;
    padding: 0;
    
    @media (min-width: 768px) {
      max-width: 600px;
      font-size: 1.1rem;
      line-height: 1.6;
    }
  }
`;

const colorTransition = keyframes`
  from {
    color: #333;
  }
  to {
    color: ${props => props.variant === 'blue' ? '#4254b5' : '#ff4b4e'};
  }
`;

const HomeBanner = () => {
  const [showText, setShowText] = useState(true)
  const [fadeOutText, setFadeOutText] = useState(false)
  const [showToggleTitle, setShowToggleTitle] = useState(false)
  const [showToggles, setShowToggles] = useState(false)
  const [toggleState, setToggleState] = useState({
    blueToggle: false,
    redToggle: false
  })

  useEffect(() => {
    // Start fade out after 7 seconds
    const fadeOutTimer = setTimeout(() => {
      setFadeOutText(true)
    }, 1000)

    // Show toggle title after text disappears
    const showToggleTitleTimer = setTimeout(() => {
      setShowText(false)
      setShowToggleTitle(true)
    }, 1000)

    // Show toggles 1.5 seconds after title
    const showTogglesTimer = setTimeout(() => {
      setShowToggles(true)
    }, 1300)

    return () => {
      clearTimeout(fadeOutTimer)
      clearTimeout(showToggleTitleTimer)
      clearTimeout(showTogglesTimer)
    }
  }, [])

  const handleToggle = (toggleName) => {
    if (toggleState[`${toggleName}Toggle`]) {
      // If clicking an open toggle, close it
      setToggleState({
        blueToggle: false,
        redToggle: false
      });
    } else {
      // If clicking a closed toggle, close the other and open this one
      setToggleState({
        blueToggle: toggleName === 'blue',
        redToggle: toggleName === 'red'
      });
    }
  };

  return (
    <>
      <BannerContainer>
        {/* {showText && (
          <MatrixText fadeOut={fadeOutText}>
            <TypewriterText text="I know… choosing the right IT vendor can feel like navigating a labyrinth of uncertainty. But trust me, Morpheus is, and always will be, the one you can rely on.">
              I know… choosing the right IT vendor can feel like navigating a labyrinth of uncertainty. But trust me, Morpheus is, and always will be, the one you can rely on.
            </TypewriterText>
          </MatrixText>
        )} */}
{/*         
        <ToggleTitle show={showToggleTitle}>
          <h2>Morpheus can statisfy all types of business needs</h2>
        </ToggleTitle> */}

        <ContentContainer show={showToggles}>
          <Section 
            bgColor={toggleState.blueToggle ? '#f0f2ff' : 'transparent'}
            show={showToggles}
            delay="0.2s"
            variant="blue"
            isActive={toggleState.blueToggle}
          >
            <AnimatedToggleWrapper variant="blue">
              <Toggle 
                isChecked={toggleState.blueToggle}
                onChange={() => handleToggle('blue')}
                variant="blue"
              />
            </AnimatedToggleWrapper>
             <h2>professionalism: Functionality & Reliability First</h2>
            {toggleState.blueToggle && (
              <div className='professionalism-container'>
                <StyledList>
                  <ListItem delay={1.2} variant="blue">
                    <h3>Tailored Functionality Solutions</h3>
                    <p>At Morpheus, we provide customised functionality solutions BUSINESS-ORIENTED to your needs.</p>
                  </ListItem>
                  <ListItem delay={1.4} variant="blue">
                    <h3>Unwavering Reliability & Security</h3>
                    <p>At Morpheus, products are built with the highest standards of security and reliability to minimise maintenance demands, downtime and security risks.</p>
                  </ListItem>
                  <ListItem delay={1.6} variant="blue">
                    <h3>Ongoing Support & Maintenance</h3>
                    <p>At Morpheus, we wish to grow with every client together by providing ongoing and timely support and maintenance.</p>
                  </ListItem>
                </StyledList>
              </div>
            )}
          </Section>

          <Section 
            bgColor={toggleState.redToggle ? '#fff0f0' : 'transparent'}
            show={showToggles}
            delay="0.4s"
            variant="red"
            isActive={toggleState.redToggle}
          >
            <AnimatedToggleWrapper variant="red">
              <Toggle 
                isChecked={toggleState.redToggle}
                onChange={() => handleToggle('red')}
                variant="red"
              />
            </AnimatedToggleWrapper>
             <h2>Disruptor: Creativity & Differentiation First</h2>
            {toggleState.redToggle && (
              <div className='disruptor-container'>
                 <StyledList>
                  <ListItem delay={1.2} variant="red">
                    <h3>Idea to Product</h3>
                    <p>At Morpheus, one creative idea can be transformed into a product.</p>
                  </ListItem>
                  <ListItem delay={1.4} variant="red">
                    <h3>Disruptive Innovation</h3>
                    <p>At Morpheus, we are not only DEVELOPING, but also dare to disrupt the market with you.</p>
                  </ListItem>
                  <ListItem delay={1.6} variant="red">
                    <h3>Standard Development Process, Flexible Mindset</h3>
                    <p>At Morpheus, we apply a standard development process to deliver products timely and cost-efficiently, but we DON'T templatize products.</p>
                  </ListItem>
                </StyledList>
              </div>
              
            )}
          </Section>
        </ContentContainer>
      </BannerContainer>
      {/* {showToggles && <StatsBar />} */}
    </>
  )
}

export default HomeBanner